import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Textarea, Button, Icon, useDisclosure, FormControl, FormLabel, Spinner, Text, VStack, Flex, Input, HStack } from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { FaEye, FaSave } from "react-icons/fa";
import Artifacts, { ScenarioArtifactItem, ScenarioArtifactProto } from "components/agentChat/Artifacts";
import SplitView from "components/common/SplitView";
import Chat, { ChatMessage } from "components/agentChat/Chat";
import { deleteChatSession, fetchChatSession, saveSession } from "services/AgentChatService";
import { ArtifactProto, ChatClientType } from "build-ts/chat_session";
import { artifactTypeFromJSON, ChatMessageType, chatMessageTypeFromJSON } from "build-ts/common";
import { NodeInsights } from "containers/Interfaces";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { getKGObservationData } from "services/AnalysisTreeServices";
import { ArtifactType } from "build-ts/common";
import showToast from "hooks/useCustomToast";
import { populateArtifact } from "services/DataService";
import { KGDataFrameProto } from "build-ts/dataframe";
import CopyButton from "components/common/CopyButton";
import { useAnalystChat } from "context/AnalystChatContext";
import { useURLContext } from "context/URLContext";
import { useUser } from "context/UserContext";
import ChatNameComponent from "components/agentChat/ChatNameComponent";
import { getAnalyst, putScenarios } from "services/AnalystService";
import { AnalystProto, ScenarioAnalysis } from "build-ts/analyst";
import { Header } from "build-ts/metadata";

interface ChatPageProps {
	systemPrompt?: string;
	// Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
	isFromEva?: boolean;
	initialChatSessionId?: string; // Session ID from EvaPlayground
	// Scenario creation mode (when set, chat is used for scenario creation with analyst/scenario context)
	/** The analyst id we navigated from (used to set selected analyst) */
	initialAnalystId?: string;
	// Selected scenario ids from the chat menu
	selectedScenarioIds?: string[];
	// Called when user selects scenarios from the chat menu (empty = all scenarios)
	onScenarioIdsChange?: (scenarioIds: string[]) => void;
	/** Called to close chat and return to analyses */
	onBackToAnalystScenarios?: () => void;
	/** If set, chat will initialize a session and send this as the first user message */
	initialPrompt?: string;
}

const ChatPage: React.FC<ChatPageProps> = ({
	systemPrompt = "",
	isFromEva = false,
	initialChatSessionId,
	initialAnalystId,
	selectedScenarioIds,
	onScenarioIdsChange,
	onBackToAnalystScenarios,
	initialPrompt: initialPromptProp,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { id: chatSessionId } = useParams();
	const { defaultAnalystId } = useUser();
	// Check if this is a new chat route
	const isNewChatRoute = location.pathname === "/chat/new";
	const isEvaChatRoute = location.pathname.startsWith("/eva/chat");

	const isScenarioCreationMode = initialAnalystId !== undefined || onBackToAnalystScenarios !== undefined;

	// Get chat context
	const {
		fetchChatSessionsList,
		fetchEvaChatSessionsList,
		selectedAnalyst,
		setSelectedAnalyst,
		analystsList,
		isLoadingAnalysts,
		setCurrentChatId,
		setIsNavbarCollapsed,
		currentSessionHeader,
		setCurrentSessionHeader,
	} = useAnalystChat();

	// Get chatAnalystId from URL context (for navigation from AnalystPage)
	const { chatAnalystId } = useURLContext();

	// Handle chatAnalystId from URL (when navigating from AnalystPage's Chat button)
	useEffect(() => {
		if (isScenarioCreationMode) return;
		// Only process if chatAnalystId is in the current URL query params
		const urlHasChatAnalystId = location.search.includes("chatAnalystId");

		if (!urlHasChatAnalystId || !chatAnalystId || isFromEva) {
			return;
		}

		// Wait for analysts list to be loaded
		if (isLoadingAnalysts || analystsList.length === 0) {
			return;
		}

		// Find the analyst by ID
		const analyst = analystsList.find(a => a.id === chatAnalystId);

		if (analyst) {
			// Set the analyst and navigate to /chat/new to create a new session
			setSelectedAnalyst(analyst);
			navigate("/chat/new", { replace: true });
		} else {
			// Analyst not found - show error and navigate to /chat/new without selection
			showToast("Analyst not found", "", "error");
			navigate("/chat/new", { replace: true });
		}
	}, [chatAnalystId, analystsList, isLoadingAnalysts, isFromEva, isScenarioCreationMode]);

	// Handle analyst selection
	const handleAnalystChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const analystId = e.target.value;
		const analyst = analystsList.find(a => a.id === analystId);
		setSelectedAnalyst(analyst || null);
		navigate("/chat/new");
	};

	// Loading state for chat
	const [isLoadingChat, setIsLoadingChat] = useState(false);
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	// Store all artifacts from all messages for dataframe link matching
	const [allArtifacts, setAllArtifacts] = useState<ArtifactProto[]>([]);

	// System prompt modal state
	const { isOpen: isSystemPromptModalOpen, onOpen: onSystemPromptModalOpen, onClose: onSystemPromptModalClose } = useDisclosure();
	// Save chat modal state
	const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
	const [saveChatTitle, setSaveChatTitle] = useState("");
	const [saveChatDescription, setSaveChatDescription] = useState("");
	// Artifacts panel state
	const [isArtifactsPanelVisible, setIsArtifactsPanelVisible] = useState(false);
	const [selectedArtifacts, setSelectedArtifacts] = useState<{
		artifact?: ArtifactProto,
		nodeInsights?: NodeInsights,
		kgDataFrameData?: KGDataFrameProto,
		langgraphMessage?: string[],
		orderIndex: number,
		loading: boolean
	}[]>([]);
	const [isLoadingArtifact, setIsLoadingArtifact] = useState(false);
	const [collapseAllExcept, setCollapseAllExcept] = useState<{ except: number; tick: number } | undefined>(undefined);
	const orderCounter = useRef(0);
	// Scenario artifacts panel: maps scenarioId -> orderIndex for stable sort
	const scenarioOrderMapRef = useRef<Map<string, number>>(new Map());
	const [hiddenScenarioIds, setHiddenScenarioIds] = useState<Set<string>>(new Set());
	// Current analyst for chat: full or filtered by selectedScenarioIds; state so we can update locally after put (update)
	const [currentAnalyst, setCurrentAnalyst] = useState<AnalystProto | undefined>(undefined);


	// Map of dataframe_name → sourceJson (the AI message JSON string containing its build_dataframe call).
	// Used to show the Plan in DataFrameDisplayComponent
	// Will match the dataframe_name in the DataFrameDisplayComponent to show the Plan
	const dataframePlanMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const msg of chatMessages) {
			for (const json of msg.langgraph_message_json_list ?? []) {
				try {
					const parsed = JSON.parse(json);
					if (parsed.type === "ai" && Array.isArray(parsed.tool_calls)) {
						for (const tc of parsed.tool_calls) {
							if (tc.args?.dataframe_name) {
								map[tc.args.dataframe_name] = json;
							}
						}
					}
				} catch {
					// ignore malformed JSON
				}
			}
		}
		return map;
	}, [chatMessages]);

	// Scenario creation mode: load analyst and set currentAnalyst when selectedScenarioIds is set
	useEffect(() => {
		if (!initialAnalystId) return;
		getAnalyst(initialAnalystId).then((analyst) => {
			if (!analyst) {
				setCurrentAnalyst(undefined);
				return;
			}
			const ids = selectedScenarioIds ?? [];
			if (ids.length === 0) {
				setCurrentAnalyst(analyst);
				return;
			}
			const scenarios =
				analyst.scenarios?.filter((s) => ids.includes(String(s.id ?? ""))) ?? [];
			setCurrentAnalyst({ ...analyst, scenarios });
		});
	}, [initialAnalystId, selectedScenarioIds]);

	const scenarioArtifacts = useMemo(() => {
		if (!isScenarioCreationMode) return [];
		// One artifact per scenario id: prefer the most recently-added artifact
		const byId = new Map<string, ScenarioArtifactProto>();
		for (let i = allArtifacts.length - 1; i >= 0; i--) {
			const artifact = allArtifacts[i];
			if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.SCENARIO) continue;
			const scenarioArtifact = artifact as ScenarioArtifactProto;
			if (!scenarioArtifact.scenario) continue;
			const rawId = scenarioArtifact.scenario.id;
			const scenarioId = rawId !== undefined && rawId !== null ? String(rawId) : "";
			if (!scenarioId || byId.has(scenarioId)) continue;
			byId.set(scenarioId, scenarioArtifact);
		}
		return Array.from(byId.values()).reverse();
	}, [allArtifacts, isScenarioCreationMode]);

	// Build ScenarioArtifactItem[] for Artifacts panel with orderIndex (scenario creation mode only)
	const scenarioArtifactsForPanel = useMemo((): ScenarioArtifactItem[] => {
		if (!isScenarioCreationMode) return [];
		return scenarioArtifacts
			.filter((artifact) => {
				const rawId = artifact.scenario?.id;
				const scenarioId = rawId !== undefined && rawId !== null ? String(rawId) : "";
				if (!scenarioId) return true;
				return !hiddenScenarioIds.has(scenarioId);
			})
			.map((artifact) => {
				const rawId = artifact.scenario?.id;
				const scenarioId = rawId !== undefined && rawId !== null ? String(rawId) : "";
				if (scenarioId && !scenarioOrderMapRef.current.has(scenarioId)) {
					// Allocate a new slot — this is a safety net, should rarely hit
					orderCounter.current++;
					scenarioOrderMapRef.current.set(scenarioId, orderCounter.current);
				}
				return {
					artifact,
					orderIndex: scenarioId
						? (scenarioOrderMapRef.current.get(scenarioId) ?? 0)
						: ++orderCounter.current,
				};
			});
	}, [scenarioArtifacts, isScenarioCreationMode, hiddenScenarioIds]);

	// Set of scenario ids already persisted in the analyst (Saved vs Draft badge)
	const savedScenarioIds = useMemo(() => {
		if (!isScenarioCreationMode) return new Set<string>();
		const ids = new Set<string>();
		(currentAnalyst?.scenarios ?? []).forEach((s) => {
			if (s.id !== undefined && s.id !== null) ids.add(String(s.id));
		});
		return ids;
	}, [isScenarioCreationMode, currentAnalyst?.scenarios]);

	const scenariosList: Header[] = useMemo(() => {
		if (!isScenarioCreationMode) return [];
		const scenarios = currentAnalyst?.scenarios ?? [];
		const items: Header[] = [{ id: "", name: "All scenarios" }];
		scenarios.forEach((s) => {
			items.push({
				id: String(s.id ?? ""),
				name: (s.issue?.trim() || `Scenario ${s.id ?? "?"}`) as string,
			});
		});
		return items;
	}, [isScenarioCreationMode, currentAnalyst?.scenarios]);

	const isLoadingScenarios = isScenarioCreationMode && !!initialAnalystId && currentAnalyst === undefined;

	const [chatId, setChatId] = useState<string | undefined>(undefined);
	const [isSaving, setIsSaving] = useState(false);
	// System prompt state - initialized from prop, can be updated from saved chat
	const [currentSystemPrompt, setCurrentSystemPrompt] = useState<string>(systemPrompt);
	// Chat is saved if session header has created_date
	const [isChatSaved, setIsChatSaved] = useState(false);
	// Handle artifact click
	const handleArtifactClick = async (artifact: ArtifactProto) => {
		// Check if artifact already selected
		const existingIndex = selectedArtifacts.findIndex(
			item => item.artifact === artifact
		);

		if (existingIndex !== -1) {
			// Artifact already exists - update its orderIndex to bring it to top
			orderCounter.current++;
			setSelectedArtifacts(prev =>
				prev.map((item, idx) =>
					idx === existingIndex
						? { ...item, orderIndex: orderCounter.current }
						: item
				)
			);
			setCollapseAllExcept(prev => ({ except: orderCounter.current, tick: (prev?.tick ?? 0) + 1 }));
			setIsArtifactsPanelVisible(true);
			setIsNavbarCollapsed(true);
			return;
		}

		const artifactType = artifactTypeFromJSON(artifact.artifact_type);

		// Handle KG_DATAFRAME
		if (artifactType === ArtifactType.KG_DATAFRAME) {
			if (!artifact.kg_dataframe) {
				showToast("Missing kg_dataframe data", "", "error");
				return;
			}

			// Check if any column has data_value_json_array
			const hasData = artifact.kg_dataframe.ordered_columns.some(
				column => column.data_value_json_array
			);

			// Show panel and set loading
			setIsNavbarCollapsed(true);
			setIsArtifactsPanelVisible(true);

			if (hasData) {
				// Data is already present, add artifact directly
				orderCounter.current++;
				setSelectedArtifacts(prev => [...prev, {
					artifact,
					kgDataFrameData: artifact.kg_dataframe,
					orderIndex: orderCounter.current,
					loading: false
				}]);
				setCollapseAllExcept(prev => ({ except: orderCounter.current, tick: (prev?.tick ?? 0) + 1 }));
			} else {
				// No data present, need to fetch it
				setIsLoadingArtifact(true);

				try {
					const artifactRequest = ArtifactProto.create({
						artifact_type: ArtifactType.KG_DATAFRAME,
						kg_dataframe: artifact.kg_dataframe
					});
					const response = await populateArtifact(artifactRequest);
					const dataframeData = response?.kg_dataframe;

					if (dataframeData) {
						orderCounter.current++;
						setSelectedArtifacts(prev => [...prev, {
							artifact,
							kgDataFrameData: dataframeData,
							orderIndex: orderCounter.current,
							loading: false
						}]);
						setCollapseAllExcept(prev => ({ except: orderCounter.current, tick: (prev?.tick ?? 0) + 1 }));
					} else {
						showToast("Failed to fetch dataframe data", "", "error");
					}
				} catch (error) {
					console.error("Error fetching dataframe data:", error);
					showToast("Error fetching dataframe data", "", "error");
				} finally {
					setIsLoadingArtifact(false);
				}
			}
			return;
		}

		// Handle KG_OBSERVATION
		if (artifactType === ArtifactType.KG_OBSERVATION) {
			if (!artifact.kg_observation) {
				showToast("Missing kg_observation data", "", "error");
				return;
			}

			// Show panel and set loading
			setIsLoadingArtifact(true);
			setIsArtifactsPanelVisible(true);

			try {
				// Call API with single observation
				const response = await getKGObservationData([artifact.kg_observation]);

				if (response && response.length > 0 && response[0].kg_observation_data) {
					// Build NodeInsights
					const nodeInsights: NodeInsights = {
						current_node: AnalysisTreeNodeProto.create({
							kg_observation: artifact.kg_observation,
							kg_observation_data: response[0].kg_observation_data
						}),
						insights: undefined
					};

					// Add to selected artifacts
					orderCounter.current++;
					setSelectedArtifacts(prev => [...prev, {
						artifact,
						nodeInsights,
						orderIndex: orderCounter.current,
						loading: false
					}]);
				}
			} catch (error) {
				console.error("Error fetching artifact data:", error);
			} finally {
				setIsLoadingArtifact(false);
			}
			return;
		}

		// Handle SCENARIO (scenario creation mode): show/hide scenario cards in artifacts panel
		if (artifactType === ArtifactType.SCENARIO && isScenarioCreationMode) {
			const sa = artifact as ScenarioArtifactProto;
			const rawId = sa.scenario?.id;
			const scenarioId = rawId !== undefined && rawId !== null ? String(rawId) : "";
			if (scenarioId) {
				setHiddenScenarioIds((prev) => {
					if (!prev.has(scenarioId)) return prev;
					const next = new Set(prev);
					next.delete(scenarioId);
					return next;
				});
			}
			setIsArtifactsPanelVisible(true);
			setIsNavbarCollapsed(true);
			return;
		}

		// Unsupported artifact type
		showToast("Artifact type not supported yet", "", "error");
	};

	// Handle langgraph message click
	const handleLanggraphMessageClick = (langgraphMessageJsonList: string[]) => {
		if (langgraphMessageJsonList.length === 0) {
			showToast("No langgraph messages to display", "", "error");
			return;
		}

		// Check if langgraph message already exists in selectedArtifacts
		const existingIndex = selectedArtifacts.findIndex(
			item => item.langgraphMessage !== undefined
		);

		if (existingIndex !== -1) {
			// Already exists - update orderIndex to bring to top
			orderCounter.current++;
			setSelectedArtifacts(prev =>
				prev.map((item, idx) =>
					idx === existingIndex
						? { ...item, langgraphMessage: langgraphMessageJsonList, orderIndex: orderCounter.current }
						: item
				)
			);
		} else {
			// Add new langgraph message
			orderCounter.current++;
			setSelectedArtifacts(prev => [...prev, {
				langgraphMessage: langgraphMessageJsonList,
				orderIndex: orderCounter.current,
				loading: false
			}]);
		}

		setIsArtifactsPanelVisible(true);
		setIsNavbarCollapsed(true);
	};

	// Effect to close artifacts panel when loading chat
	useEffect(() => {
		if (isLoadingChat) {
			handleCloseArtifactsPanel();
			setSelectedArtifacts([]);
		}
	}, [isLoadingChat]);

	// Handle closing artifacts panel
	const handleCloseArtifactsPanel = () => {
		setIsArtifactsPanelVisible(false);
	};

	// Handle clearing all artifacts
	const handleClearAllArtifacts = () => {
		setSelectedArtifacts([]);
	};

	// Handle removing a single artifact
	const handleRemoveArtifact = (artifactToRemove: ArtifactProto) => {
		setSelectedArtifacts(prev => prev.filter(item => item.artifact !== artifactToRemove));
	};

	// Handle removing langgraph message
	const handleRemoveLanggraphMessage = () => {
		setSelectedArtifacts(prev => prev.filter(item => item.langgraphMessage === undefined));
	};

	// Save from Artifacts panel: called by ScenarioArtifactCard when user clicks Save/Update
	const handleSaveScenarioFromPanel = async (
		scenarioToSave: ScenarioAnalysis
	) => {
		if (!initialAnalystId) return;
		if (!scenarioToSave.issue?.trim()) {
			showToast("Please provide an issue description", "", "warning");
			return;
		}
		if (!currentAnalyst) {
			showToast("Failed to load analyst", "", "error");
			return;
		}
		const existing = currentAnalyst.scenarios ?? [];
		const scenarioId = scenarioToSave.id;
		const existingScenarioToUpdate =
			scenarioId !== undefined && scenarioId !== null
				? existing.find((s) => String(s.id) === String(scenarioId))
				: undefined;

		const scenarioToPut = existingScenarioToUpdate
			? { ...scenarioToSave, id: existingScenarioToUpdate.id }
			: scenarioToSave;

		const result = await putScenarios(initialAnalystId, [scenarioToPut]);
		if (!result) return;

		// Use returned analyst from putScenarios (same filter as initial load)
		const ids = selectedScenarioIds ?? [];
		if (ids.length === 0) {
			setCurrentAnalyst(result);
		} else {
			setCurrentAnalyst((prev) => {
				const prevScenarios = prev?.scenarios ?? [];
				if (!scenarioToPut) return prev ?? { scenarios: prevScenarios };
				const idx = prevScenarios.findIndex(
					(s) => String(s.id) === String(scenarioToPut.id)
				);
				const scenarios =
					idx >= 0 ? [
						...prevScenarios.slice(0, idx),
						scenarioToPut,
						...prevScenarios.slice(idx + 1),
					] : [...prevScenarios, scenarioToPut];
				return { ...prev, scenarios };
			});
		}

		showToast("Scenario saved successfully", "", "success");
		if (scenarioToPut === null) return;
		setAllArtifacts(prev => prev.map(artifact =>
			artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.SCENARIO && artifact.scenario?.id === scenarioToPut.id
				? { ...artifact, scenario: scenarioToPut }
				: artifact
		));
	};

	// Remove from Artifacts panel: called by ScenarioArtifactCard when user clicks remove
	const handleRemoveScenarioFromPanel = (scenarioId: string) => {
		setHiddenScenarioIds((prev) => {
			const next = new Set(prev);
			next.add(scenarioId);
			return next;
		});
	};

	// Update currentSystemPrompt when systemPrompt prop changes (for playground)
	useEffect(() => {
		if (systemPrompt) {
			setCurrentSystemPrompt(systemPrompt);
		}
	}, [systemPrompt]);

	// Set chatId from initialChatSessionId when coming from EvaPlayground
	useEffect(() => {
		if (isFromEva && !isChatSaved && initialChatSessionId && !chatId) {
			setChatId(initialChatSessionId);
		}
	}, [isFromEva, isChatSaved, initialChatSessionId, chatId, navigate]);

	// Scenario creation mode: reset chat state on mount
	useEffect(() => {
		if (!isScenarioCreationMode) return;
		setChatMessages([]);
		setAllArtifacts([]);
		setChatId(undefined);
		setHiddenScenarioIds(new Set());
	}, [isScenarioCreationMode]);

	// Scenario creation mode: when scenario selection changes, start new chat with that context
	useEffect(() => {
		if (!isScenarioCreationMode) return;
		setChatId(undefined);
		setChatMessages([]);
		setAllArtifacts([]);
		setHiddenScenarioIds(new Set());
	}, [selectedScenarioIds, isScenarioCreationMode]);

	// Scenario creation mode: auto-open artifacts panel when first scenario artifact appears
	useEffect(() => {
		if (!isScenarioCreationMode || scenarioArtifacts.length === 0 || isArtifactsPanelVisible) return;
		setIsArtifactsPanelVisible(true);
		setIsNavbarCollapsed(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isScenarioCreationMode, scenarioArtifacts.length]);

	// This is used to clean the content of the chat messages from the ```markdown or ``` wrapper
	const cleanContent = (raw: string): string => {
		// Remove ```markdown or ``` wrapper
		return raw.replace(/^```(?:markdown)?\n?/, "").replace(/\n?```$/, "");
	};

	// Function to fetch the chat history - new darwin
	const fetchChatHistory = async (sessionId: string) => {
		const chatResponse = await fetchChatSession(sessionId, true);
		if (chatResponse?.session_header?.id) {
			setChatId(chatResponse.session_header.id);
			setCurrentChatId(chatResponse.session_header.id);
			setCurrentSessionHeader(chatResponse.session_header);
			// Set isChatSaved to true if chat is saved
			if (chatResponse.session_header.created_date) {
				setIsChatSaved(true);
			}
		}

		// Extract system prompt from first SYSTEM message for saved chats
		let systemPromptFromMessage = "";
		const convertedMessages: ChatMessage[] = [];
		const collectedArtifacts: ArtifactProto[] = [];
		let pendingArtifacts: ArtifactProto[] | undefined = [];
		let langgraphMessageJsonList: string[] = [];

		for (const msg of chatResponse.messages) {
			// Extract system prompt from first SYSTEM message
			if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.SYSTEM && msg.content && !systemPromptFromMessage) {
				systemPromptFromMessage = msg.content;
			}
			else if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content) {
				convertedMessages.push({
					author: "user",
					content: cleanContent(msg.content)
				});
			}
			else if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING) {
				// Store artifacts and langgraph message json list to attach to the next RESPONSE message
				pendingArtifacts = msg.artifacts && msg.artifacts.length > 0 ? msg.artifacts : undefined;
				langgraphMessageJsonList = msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0 ? msg.langgraph_message_json_list : [];

				// Collect artifacts (excluding langgraph messages)
				if (pendingArtifacts) {
					for (const artifact of pendingArtifacts) {
						const artifactType = artifactTypeFromJSON(artifact.artifact_type);
						if (artifactType === ArtifactType.KG_DATAFRAME || artifactType === ArtifactType.KG_OBSERVATION) {
							collectedArtifacts.push(artifact);
						}
					}
				}
			}
			else if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.RESPONSE && msg.content) {
				convertedMessages.push({
					author: "ai",
					content: cleanContent(msg.content),
					artifacts: pendingArtifacts,  // Attach pending artifacts from REASONING
					ls_trace_url: msg.ls_trace_url,
					langgraph_message_json_list: langgraphMessageJsonList
				});
				pendingArtifacts = [];  // Clear after attaching
				langgraphMessageJsonList = [];
			}
			else if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.RESPONSE && msg.response_message) {
				convertedMessages.push({
					author: "ai",
					content: "",
					responseMessage: msg.response_message,
					artifacts: pendingArtifacts,  // Attach pending artifacts from REASONING
					ls_trace_url: msg.ls_trace_url,
					langgraph_message_json_list: langgraphMessageJsonList
				});
				pendingArtifacts = [];
				langgraphMessageJsonList = [];
			}
		}
		// Update system prompt from API if found (for saved chats)
		if (systemPromptFromMessage) {
			setCurrentSystemPrompt(systemPromptFromMessage);
		}
		setChatMessages(convertedMessages);
		setAllArtifacts(collectedArtifacts);
	};

	// Effect to handle URL changes: /chat/new or /chat/:id or /eva/chat/:id (skip in scenario creation mode)
	useEffect(() => {
		if (isScenarioCreationMode) return;
		// Handle EVA Chat case
		if (isEvaChatRoute && chatSessionId && chatSessionId !== chatId) {
			setChatId(chatSessionId);
			setIsLoadingChat(true);
			fetchChatHistory(chatSessionId)
				.catch(error => console.error("Error loading chat session:", error))
				.finally(() => setIsLoadingChat(false));
			return;
		}

		// // Handle /chat/new route - initialize a new chat session
		if (isNewChatRoute) {
			setChatMessages([]);
			setAllArtifacts([]);
			setSelectedArtifacts([]);
			setIsArtifactsPanelVisible(false);
			setChatId(undefined);
			setIsChatSaved(false);
			setCurrentSessionHeader(undefined);
			setCurrentChatId("");
			setSelectedAnalyst(analystsList.find(analyst => analyst.id === defaultAnalystId) || analystsList[0]);
			return;
		}

		// Handle /chat/:id route - load existing chat session
		if (chatSessionId && chatSessionId !== chatId && !isEvaChatRoute) {
			setIsLoadingChat(true);
			fetchChatHistory(chatSessionId)
				.catch(error => console.error("Error loading chat session:", error))
				.finally(() => setIsLoadingChat(false));
		}
	}, [isNewChatRoute, chatSessionId, analystsList, initialChatSessionId, isScenarioCreationMode]);

	return (
		<Box height="100%" width="100%" overflow="hidden" position="relative" display="flex" flexDirection="column">

			{/* Scenario creation: back button (to Analyst Scenarios) */}
			{isScenarioCreationMode && onBackToAnalystScenarios && (
				<Box
					position="absolute"
					top={3}
					left={4}
					zIndex={10}
					pointerEvents="none"
				>
					<Button
						pointerEvents="auto"
						leftIcon={<ChevronLeftIcon boxSize={4} />}
						onClick={onBackToAnalystScenarios}
						size="sm"
						bg="white"
						color="gray.700"
						border="1px solid"
						borderColor="gray.200"
						boxShadow="sm"
						_hover={{ bg: "gray.50", borderColor: "gray.300" }}
						_active={{ bg: "gray.100" }}
						borderRadius="lg"
						fontWeight="medium"
						fontSize="sm"
					>
						Back to Analyst Scenarios
					</Button>
				</Box>
			)}
			{/* Overlay: EVA buttons (only shown in EvaPlayground mode) */}
			{!isScenarioCreationMode && isFromEva && (
				<Box
					position="absolute"
					top={3}
					left={4}
					zIndex={10}
				>
					<Flex gap={2}>

						{isFromEva && (
							<HStack spacing={2}>
								{/* EVA saved chat — name header with rename/delete */}
								{isChatSaved && currentSessionHeader && (
									<ChatNameComponent
										chatId={currentSessionHeader?.id || ""}
										sessionHeader={currentSessionHeader}
										setSessionHeader={setCurrentSessionHeader}
										onAfterRename={() => fetchEvaChatSessionsList("")}
										onDelete={() => {
											if (!currentSessionHeader?.id) return;
											deleteChatSession(currentSessionHeader.id).then(() => {
												fetchEvaChatSessionsList("");
												navigate("/eva/agents");
											});
										}}
									/>
								)}

								<Button
									leftIcon={<Icon as={FaEye} boxSize={3} />}
									onClick={onSystemPromptModalOpen}
									size="sm"
									bg="white"
									color="gray.700"
									border="1px solid"
									borderColor="gray.200"
									boxShadow="sm"
									_hover={{ bg: "gray.50", borderColor: "gray.300" }}
									_active={{ bg: "gray.100" }}
									borderRadius="lg"
									fontWeight="medium"
									fontSize="sm"
								>
									View System Prompt
								</Button>
							</HStack>
						)}
						{/* Show Save Chat button only if chat is not saved and we have messages */}
						{!isChatSaved && chatMessages.length > 0 && (
							<Button
								leftIcon={<Icon as={FaSave} boxSize={3} />}
								onClick={onSaveModalOpen}
								size="sm"
								bg="white"
								color="green.600"
								border="1px solid"
								borderColor="green.200"
								boxShadow="sm"
								_hover={{ bg: "green.50", borderColor: "green.300" }}
								_active={{ bg: "green.100" }}
								borderRadius="lg"
								fontWeight="medium"
								fontSize="sm"
							>
								Save Chat
							</Button>
						)}
					</Flex>
				</Box>
			)}

			{/* Main Content Area */}
			<Box
				flex={1}
				width="100%"
				transition="margin-left 0.3s ease-in-out, width 0.3s ease-in-out"
				overflow="hidden"
			>
				<SplitView
					leftPanel={
						<Box
							height="100%"
							width="100%"
							bg="bg.surface"
							overflow="hidden"
							display="flex"
							flexDirection="column"
						>
							{!isScenarioCreationMode && !isFromEva && currentSessionHeader && (
								<ChatNameComponent
									chatId={currentSessionHeader?.id || ""}
									sessionHeader={currentSessionHeader}
									setSessionHeader={setCurrentSessionHeader}
									onAfterRename={() => fetchChatSessionsList(ChatClientType.FRIDA, "")}
									onDelete={() => {
										if (!currentSessionHeader?.id) return;
										deleteChatSession(currentSessionHeader?.id).then(() => {
											fetchChatSessionsList(ChatClientType.FRIDA, "");
											navigate("/chat/new");
										});
									}}
									onToggleArtifacts={() => setIsArtifactsPanelVisible((v) => !v)}
									isArtifactsPanelVisible={isArtifactsPanelVisible}
								/>
							)}
							<Box flex={1} overflow="hidden">
								<Chat
									chatMessages={chatMessages}
									chatId={chatId}
									setChatId={setChatId}
									setChatMessages={setChatMessages}
									isLoadingChat={isLoadingChat}
									sessionId={isScenarioCreationMode ? "" : (isFromEva && !isChatSaved ? initialChatSessionId : chatSessionId)}
									fetchChatSessionsList={fetchChatSessionsList}
									fetchEvaChatSessionsList={fetchEvaChatSessionsList}
									onArtifactClick={handleArtifactClick}
									onLanggraphMessageClick={handleLanggraphMessageClick}
									selectedAnalyst={selectedAnalyst}
									isFromEva={isFromEva}
									cleanContent={cleanContent}
									analystsList={analystsList}
									isLoadingAnalysts={isLoadingAnalysts}
									onAnalystChange={handleAnalystChange}
									isArtifactsPanelVisible={isArtifactsPanelVisible}
									allArtifacts={allArtifacts}
									setAllArtifacts={setAllArtifacts}
									isChatSaved={isChatSaved}
									isForScenarioCreationAgentChat={isScenarioCreationMode}
									currentAnalyst={currentAnalyst}
									selectedScenarioIds={selectedScenarioIds ?? []}
									scenariosList={scenariosList}
									isLoadingScenarios={isLoadingScenarios}
									onScenarioChange={onScenarioIdsChange}
									initialPrompt={isScenarioCreationMode ? initialPromptProp : undefined}
								/>
							</Box>
						</Box>
					}
					rightPanel={
						<Artifacts
							selectedArtifacts={selectedArtifacts}
							isLoadingArtifact={isLoadingArtifact}
							onClosePanel={handleCloseArtifactsPanel}
							onClearAll={handleClearAllArtifacts}
							onRemoveArtifact={handleRemoveArtifact}
							onRemoveLanggraphMessage={handleRemoveLanggraphMessage}
							dataframePlanMap={dataframePlanMap}
							scenarioArtifacts={isScenarioCreationMode ? scenarioArtifactsForPanel : []}
							onScenarioPanelSave={isScenarioCreationMode ? handleSaveScenarioFromPanel : undefined}
							onScenarioPanelRemove={isScenarioCreationMode ? handleRemoveScenarioFromPanel : undefined}
							savedScenarioIds={isScenarioCreationMode ? savedScenarioIds : undefined}
							collapseAllExcept={collapseAllExcept}
						/>
					}
					isRightPanelVisible={isArtifactsPanelVisible}
					defaultRightWidth={45}
				/>
			</Box>

			{/* System Prompt Modal */}
			<Modal isOpen={isSystemPromptModalOpen} onClose={onSystemPromptModalClose} size="xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>
						System Prompt
						<Flex flex={1} justify="flex-end" mt={2}>
							<CopyButton value={systemPrompt} />
						</Flex>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<Textarea
							value={currentSystemPrompt}
							readOnly
							rows={25}
							resize="vertical"
							bg="gray.50"
							border="1px solid"
							borderColor="gray.200"
						/>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Save Chat Modal */}
			<Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} size="md" closeOnOverlayClick={!isSaving}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Save Chat</ModalHeader>
					{!isSaving && <ModalCloseButton />}
					<ModalBody>
						{isSaving ? (
							<VStack spacing={4} py={6}>
								<Spinner size="lg" color="green.500" thickness="3px" />
								<Text color="gray.600" fontSize="md">Saving your chat...</Text>
							</VStack>
						) : (
							<>
								<FormControl>
									<FormLabel>Chat Title</FormLabel>
									<Input
										placeholder="Enter a title for this chat..."
										value={saveChatTitle}
										onChange={(e) => setSaveChatTitle(e.target.value)}
										autoFocus
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Chat Description</FormLabel>
									<Input
										placeholder="Enter a description for this chat..."
										value={saveChatDescription}
										onChange={(e) => setSaveChatDescription(e.target.value)}
									/>
								</FormControl>
							</>
						)}
					</ModalBody>
					{!isSaving && (
						<ModalFooter>
							<Button variant="ghost" mr={3} onClick={onSaveModalClose}>
								Cancel
							</Button>
							<Button
								colorScheme="green"
								isDisabled={!saveChatTitle.trim()}
								onClick={async () => {
									if (!chatId || !saveChatTitle.trim()) return;
									setIsSaving(true);
									try {
										// Save with title in single API call
										await saveSession(chatId, saveChatTitle.trim(), saveChatDescription?.trim());
										onSaveModalClose();
										setSaveChatTitle("");
										setSaveChatDescription("");
										setCurrentSessionHeader(undefined);
										setIsChatSaved(true);
										// Refresh the chat sessions list
										if (isFromEva) {
											await fetchEvaChatSessionsList("");
											if (isFromEva && chatId) {
												navigate(`/eva/chat/${chatId}`);
											}
										} else {
											await fetchChatSessionsList(ChatClientType.FRIDA, "");
										}
									} catch (error) {
										console.error("Error saving chat:", error);
									} finally {
										setIsSaving(false);
									}
								}}
							>
								Save
							</Button>
						</ModalFooter>
					)}
				</ModalContent>
			</Modal>

		</Box>
	);
};

export default ChatPage;